import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Factory, Award, Globe, Users, Target, TrendingUp } from 'lucide-react';
import { t } from '../utils/translation';
import { useScrollAnimation, AnimatedNumber } from '../hooks/useScrollAnimation';
import factoryImage from '../assets/factory/factory-exterior.png';
import conferenceImage from '../assets/factory/conference-room.jpeg';
import PageHeader from '../components/PageHeader';
import SectionSplitter from '../components/SectionSplitter';
import { useSiteAssets } from '../context/SiteAssetsContext';

const About = ({ theme, language }) => {
  const { assets } = useSiteAssets();
  const milestoneData = [
    { year: '2011', titleKey: 'companyFounded', descKey: 'establishedSuzhou' },
    { year: '2014', titleKey: 'internationalExpansion', descKey: 'firstExports' },
    { year: '2017', titleKey: 'rohsCertification', descKey: 'achievedCompliance' },
    { year: '2020', titleKey: 'thousandClients', descKey: 'reachedMilestone' },
    { year: '2023', titleKey: 'advancedAutomation', descKey: 'upgradedFacilities' },
    { year: '2025', titleKey: 'globalLeader', descKey: 'servingCountries' }
  ];

  const valueData = [
    {
      icon: <Target className={`w-14 h-14 ${theme === 'dark' ? 'text-red-600' : 'text-red-600'}`} />,
      titleKey: 'qualityFirst',
      descKey: 'qualityFirstDesc'
    },
    {
      icon: <Users className={`w-14 h-14 ${theme === 'dark' ? 'text-red-600' : 'text-red-600'}`} />,
      titleKey: 'customerFocus',
      descKey: 'customerFocusDesc'
    },
    {
      icon: <TrendingUp className={`w-14 h-14 ${theme === 'dark' ? 'text-red-600' : 'text-red-600'}`} />,
      titleKey: 'innovation',
      descKey: 'innovationDesc'
    },
    {
      icon: <Globe className={`w-14 h-14 ${theme === 'dark' ? 'text-red-600' : 'text-red-600'}`} />,
      titleKey: 'globalReach',
      descKey: 'globalReachDesc'
    }
  ];

  const statData = [
    { number: '14+', labelKey: 'yearsInBusiness' },
    { number: '1000+', labelKey: 'globalClients' },
    { number: '8+', labelKey: 'countriesServed' },
    { number: '20+', labelKey: 'brandsReplaced' },
    { number: '50M+', labelKey: 'annualProduction' },
    { number: '100%', labelKey: 'qualityTested' }
  ];

  const translatedMilestones = milestoneData.map(milestone => ({
    year: milestone.year,
    title: t(milestone.titleKey, language),
    desc: t(milestone.descKey, language)
  }));

  const translatedValues = valueData.map(value => ({
    icon: value.icon,
    title: t(value.titleKey, language),
    desc: t(value.descKey, language)
  }));

  const translatedStats = statData.map(stat => ({
    number: stat.number,
    label: t(stat.labelKey, language)
  }));

  // Stats section with scroll animation
  const [statsRef, statsVisible] = useScrollAnimation(0.2);

  const aboutUsText = t('aboutUs', language);
  const aboutDescriptionText = t('aboutDescription', language);
  const aboutPageDescriptionText = t('aboutPageDescription', language);

  return (
    <div className={`min-h-screen transition-colors duration-300 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
      <Helmet>
        <title>{aboutUsText} - Qingshun Electronics</title>
        <meta name="description" content={aboutDescriptionText} />
        <meta name="keywords" content="about us, connector manufacturer, electronics company, China manufacturer, quality connectors, custom manufacturing" />
        <meta name="author" content="Qingshun Electronics Technology Co., Ltd." />
        <link rel="canonical" href="https://qsconn.com/about" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://qsconn.com/about" />
        <meta property="og:title" content={`${aboutUsText} | Qingshun Electronics - Leading Connector Manufacturer in China`} />
        <meta property="og:description" content={aboutDescriptionText} />
        <meta property="og:image" content="https://qsconn.com/logo512.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://qsconn.com/about" />
        <meta property="twitter:title" content={`${aboutUsText} | Qingshun Electronics - Leading Connector Manufacturer in China`} />
        <meta property="twitter:description" content={aboutDescriptionText} />
        <meta property="twitter:image" content="https://qsconn.com/logo512.png" />
        <meta property="twitter:image:alt" content={`${aboutUsText} - Qingshun Electronics`} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": ["AboutPage", "WebPage"],
            "name": aboutUsText,
            "description": aboutPageDescriptionText,
            "url": "https://qsconn.com/about",
            "inLanguage": "en"
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {"@type": "ListItem", "position": 1, "name": "Home", "item": "https://qsconn.com/"},
              {"@type": "ListItem", "position": 2, "name": aboutUsText, "item": "https://qsconn.com/about"}
            ]
          })}
        </script>
      </Helmet>

      <PageHeader
        theme={theme}
        badge={t('about', language)}
        title={`${t('about', language)} Qingshun`}
        subtitle={t('leadingManufacturer', language)}
      />

      {/* Company Overview */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className={`text-4xl md:text-5xl font-extrabold mb-6 transition-colors duration-300 ${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`}>
                {t('whoWeAre', language)}
              </h2>
              <div className={`space-y-5 text-xl leading-relaxed transition-colors duration-300 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-900'}`}>
                <p>
                  {t('companyDescription1', language)}
                </p>
                <p>
                  {t('companyDescription2', language)}
                </p>
                <p>
                  {t('productsServe', language)}
                </p>
              </div>
            </div>
            <div className={`rounded-3xl p-9 shadow-xl transition-all duration-300 ${theme === 'dark' ? 'bg-black border-2 border-gray-800' : 'bg-white border-2 border-gray-300'}`}>
              <div className={`relative rounded-xl overflow-hidden mb-6 transition-all duration-300 ${theme === 'dark' ? 'border-2 border-gray-800' : 'border-2 border-gray-300'}`}>
                <img 
                  src={assets.about_factory || factoryImage} 
                  alt={t('factoryImageAlt', language)} 
                  className="w-full h-64 transition-transform duration-500 hover:scale-105 object-cover"
                />
                <div className={`absolute inset-0 bg-gradient-to-t transition-colors duration-300 ${theme === 'dark' ? 'from-black/70' : 'from-white/70'} to-transparent`}></div>
              </div>
              <div 
                ref={statsRef}
                className="grid grid-cols-2 gap-6"
              >
                {translatedStats.map((stat, idx) => (
                  <div key={idx} className={`text-center p-5 rounded-xl transition-all duration-300 ${theme === 'dark' ? 'bg-black border border-gray-800' : 'bg-white border border-gray-300'}`}>
                    <div className={`text-4xl font-extrabold ${theme === 'dark' ? 'text-red-400' : 'text-red-600'} mb-3 transition-colors duration-300`}>
                      <AnimatedNumber value={stat.number} isVisible={statsVisible} />
                    </div>
                    <div className={`font-semibold transition-colors duration-300 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-800'}`}>{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <SectionSplitter theme={theme} variant="wave" />

      {/* Our Values */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className={`text-4xl md:text-5xl font-extrabold mb-4 transition-colors duration-300 ${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`}>
              {t('coreValues', language)}
            </h2>
            <p className={`text-xl max-w-2xl mx-auto transition-colors duration-300 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-800'}`}>
              {t('principlesGuide', language)}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-9">
            {translatedValues.map((value, idx) => (
              <div key={idx} className={`rounded-2xl p-9 shadow-lg transition-all duration-300 transform hover:-translate-y-2 hover:bg-gradient-to-br ${theme === 'dark' ? 'bg-black border-2 border-gray-800 hover:border-red-600 from-red-900/10 to-red-900/5' : 'bg-white border-2 border-gray-300 hover:border-red-600 from-red-50 to-red-100'}`}>
                <div className={`mb-5 transform hover:scale-110 transition duration-300 ${theme === 'dark' ? 'text-red-600' : 'text-red-600'}`}>{value.icon}</div>
                <h3 className={`text-2xl font-extrabold mb-4 transition-colors duration-300 ${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`}>{value.title}</h3>
                <p className={`text-xl leading-relaxed transition-colors duration-300 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-800'}`}>{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <SectionSplitter theme={theme} variant="diagonal" flip />

      {/* Timeline */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className={`text-4xl md:text-5xl font-extrabold mb-4 transition-colors duration-300 ${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`}>
              {t('ourJourney', language)}
            </h2>
            <p className={`text-xl max-w-2xl mx-auto transition-colors duration-300 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-800'}`}>
              {t('keyMilestones', language)}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7">
            {translatedMilestones.map((milestone, idx) => (
              <div key={idx} className={`rounded-2xl p-9 shadow-lg transition-all duration-300 relative overflow-hidden transform hover:-translate-y-1 ${theme === 'dark' ? 'bg-black border-2 border-gray-800 hover:border-red-600' : 'bg-white border-2 border-gray-300 hover:border-red-600'}`}>
                <div className="relative z-10">
                  <h3 className={`text-2xl font-extrabold mb-3 transition-colors duration-300 ${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`}>{milestone.title}</h3>
                  <p className={`text-lg transition-colors duration-300 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-800'}`}>{milestone.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <SectionSplitter theme={theme} variant="curve" />

      {/* Certifications */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Award className={`w-20 h-20 mx-auto mb-5 ${theme === 'dark' ? 'text-red-600' : 'text-red-600'}`} />
            <h2 className={`text-4xl md:text-5xl font-extrabold mb-4 transition-colors duration-300 ${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`}>
              {t('certificationsQuality', language)}
            </h2>
            <p className={`text-xl max-w-2xl mx-auto transition-colors duration-300 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-800'}`}>
              {t('certified', language)}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-7">
            {['RoHS', 'ISO 9001'].map((cert, idx) => (
              <div key={idx} className={`text-center rounded-2xl p-9 shadow-lg transform hover:scale-105 transition-all duration-300 ${theme === 'dark' ? 'bg-black border-2 border-gray-800 hover:border-red-600' : 'bg-white border-2 border-gray-300 hover:border-red-600'}`}>
                <Award className={`w-14 h-14 mx-auto mb-5 transform hover:rotate-12 transition duration-300 ${theme === 'dark' ? 'text-red-600' : 'text-red-600'}`} />
                <div className={`text-2xl font-extrabold ${theme === 'dark' ? 'text-red-400' : 'text-red-600'} transition-colors duration-300`}>{cert}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
